/** Shared pieces of the "Hồ sơ tên miền" queue and its review panel. */

import { useTranslation } from 'react-i18next';
import type { DossierQueueRow, DossierStage } from '@/api/areas/dossierQueue';
import { Badge, type Tone } from '@/components/Badge';
import { formatDateTime, formatRelative } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useSyncErrorMeaning } from '../compliance/vnnic/shared';
import { useCheckLabel } from '../declarations/shared';

export const STAGE_TONE: Record<DossierStage, Tone> = {
  AWAITING_CUSTOMER: 'pending',
  AUTO_CHECKING: 'info',
  NEEDS_REVIEW: 'serious',
  WARNING: 'warn',
  ACTIVATING: 'ok',
  ACTIVATED: 'ok',
};

export function StageBadge({ stage }: { stage: DossierStage }) {
  const { t } = useTranslation('dossiers');
  return <Badge tone={STAGE_TONE[stage]}>{t(`stage.${stage}`)}</Badge>;
}

const FIELD_NAMES = ['name', 'document_number', 'birth'] as const;

/** "What the system found", in words: the finding and, for a review or warning, why. */
export function useFindingText() {
  const { t } = useTranslation('dossiers');
  const { t: tw } = useTranslation('work');
  const syncError = useSyncErrorMeaning();
  const checkLabel = useCheckLabel();
  return (r: Pick<DossierQueueRow, 'finding' | 'finding_kind' | 'finding_detail'>): { headline: string; reasons: string[]; tone: Tone } => {
    const d = r.finding_detail ?? {};
    const reasons: string[] = [];
    let headline = t(`finding.${r.finding}`);
    if (r.finding_kind) {
      if (r.finding_kind === 'DECLARATION_REVIEW') {
        headline = d.upload_kind === 'SCAN' ? t('reason.DECLARATION_REVIEW_SCAN') : t('reason.DECLARATION_REVIEW_SIGNED');
        if (d.everything_matches) reasons.push(t('reason.DECLARATION_REVIEW_MATCHES'));
      } else {
        const key = `reason.${r.finding_kind}`;
        headline = t(key as 'reason.EKYC_MISMATCH', { defaultValue: r.finding_kind });
      }
    }
    if (d.reason_codes?.length) {
      for (const c of d.reason_codes) reasons.push(t(`code.${c as 'NAME_MISMATCH'}`, { defaultValue: c }));
    } else {
      for (const f of FIELD_NAMES) {
        const v = d.comparison?.[f];
        if (v && v !== 'MATCH') reasons.push(t('reason.fields', { field: tw(`fieldName.${f}`), result: tw(`field.${v as 'MISMATCH'}`, { defaultValue: v }) }));
      }
    }
    if (d.failed_checks?.length) reasons.push(t('reason.failedChecks', { checks: d.failed_checks.map((code) => checkLabel({ code })).join(', ') }));
    if (d.changed?.length) reasons.push(t('reason.changed', { parts: d.changed.map((c) => t(`changedPart.${c as 'addresses'}`, { defaultValue: c })).join(', ') }));
    if (d.last_error_code) reasons.push(t('reason.syncError', syncError(d.last_error_code)));
    const tone: Tone =
      r.finding === 'AUTO_APPROVED' || r.finding === 'STAFF_APPROVED'
        ? 'ok'
        : r.finding === 'NEEDS_STAFF'
          ? 'serious'
          : r.finding === 'WARNING' || r.finding === 'VERIFICATION_FAILED' || r.finding === 'CORRECTION_REQUESTED'
            ? 'warn'
            : r.finding === 'CHECKING'
              ? 'info'
              : 'neutral';
    return { headline, reasons, tone };
  };
}

/** Waiting since + the deadline (red when less than 24 h away or past). */
export function WaitAndDeadline({ row }: { row: Pick<DossierQueueRow, 'waiting_since' | 'deadline' | 'deadline_paused' | 'due_soon' | 'overdue'> }) {
  const { t } = useTranslation('dossiers');
  return (
    <div className="grid gap-0.5 text-sm whitespace-nowrap">
      <span title={formatDateTime(row.waiting_since)}>{row.waiting_since ? formatRelative(row.waiting_since) : '—'}</span>
      {row.deadline ? (
        <span
          className={cn('text-xs', row.due_soon || row.overdue ? 'font-semibold text-critical' : 'text-muted-foreground', row.deadline_paused && 'line-through decoration-muted-foreground/60')}
          title={row.deadline_paused ? t('deadline.paused') : formatDateTime(row.deadline)}
        >
          {row.overdue ? t('deadline.overdue') : t('deadline.due', { when: formatRelative(row.deadline) })}
        </span>
      ) : null}
    </div>
  );
}
